import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { trackAddToCart, trackRemoveFromCart } from "../../analytics/analytics";

/* ─── helpers ─── */
const loadCartFromStorage = () => {
  try {
    const raw = localStorage.getItem("cartItems");
    if (!raw) return [];

    const parsed = JSON.parse(raw);

    const cleaned = (Array.isArray(parsed) ? parsed : [])
      .filter(
        (item) =>
          item?.productId &&
          item?.color?._id != null &&
          item?.size != null &&
          item?.sku != null,
      )
      .map((item) => ({
        ...item,
        basePrice: Number(item.basePrice ?? item.price ?? 0),
      }));

    if (JSON.stringify(cleaned) !== JSON.stringify(parsed)) {
      localStorage.setItem("cartItems", JSON.stringify(cleaned));
    }
    return cleaned;
  } catch {
    return [];
  }
};

/* ─── slice ─── */
const initialState = { cart: loadCartFromStorage() };

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const incoming = action.payload;

      if (!incoming?.color?._id) return;
      const normalizedBasePrice = Number(
        incoming.basePrice ?? incoming.price ?? 0,
      );

      if (!normalizedBasePrice) {
        console.warn(
          "addToCart: item added with no valid price — totals may be wrong",
          incoming,
        );
      }

      const existing = state.cart.find(
        (i) =>
          i?.productId === incoming.productId &&
          i?.color?._id === incoming.color._id &&
          i?.size === incoming.size,
      );

      if (existing) {
        const newQty = existing.quantity + (incoming.quantity || 1);
        existing.quantity = Math.min(newQty, existing.stock || 99);
      } else {
        state.cart.push({
          ...incoming,
          quantity: incoming.quantity || 1,
        });
      }
    },

    removeFromCart: (state, action) => {
      const { productId, colorId, size } = action.payload;
      state.cart = state.cart.filter(
        (i) =>
          !(
            i?.productId === productId &&
            i?.color?._id === colorId &&
            i?.size === size
          ),
      );
    },

    updateQuantity: (state, action) => {
      const { productId, colorId, size, quantity } = action.payload;
      const item = state.cart.find(
        (i) =>
          i?.productId === productId &&
          i?.color?._id === colorId &&
          i?.size === size,
      );
      if (!item) return;

      if (quantity <= 0) {
        state.cart = state.cart.filter((i) => i !== item);
      } else {
        item.quantity = Math.min(quantity, item.stock || 99);
      }
    },

    clearCart: (state) => {
      state.cart = [];
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, clearCart } =
  cartSlice.actions;

export default cartSlice.reducer;

/* ─── thunks ─── */
export const addToCartAsync = (cartItem) => (dispatch, getState) => {
  dispatch(addToCart(cartItem));

  // Extract properties safely with fallback defaults
  const productId = cartItem?.productId || cartItem?._id || cartItem?.id;
  const productName = cartItem?.name || cartItem?.title || "Unknown Product";
  const price = Number(cartItem?.price) || 0;
  const quantity = cartItem?.quantity || 1;

  // Track if we at least have a valid product identifier
  if (productId) {
    try {
      const colorName = cartItem?.color?.name || cartItem?.color || "";
      const sizeName = cartItem?.size || "";
      const fallbackVariant = [colorName, sizeName].filter(Boolean).join(" / ");

      trackAddToCart(
        {
          item_id: productId, // Standard GA4 parameter key
          id: productId, // Backward compatibility key
          item_name: productName,
          name: productName,
          item_brand: cartItem?.brand || "",
          item_category: cartItem?.category || "",
          item_variant: cartItem?.variant || fallbackVariant,
          price: price,
          currency: cartItem?.currency || "USD",
        },
        quantity,
      );
    } catch (error) {
      console.error("GA4 trackAddToCart failed:", error);
    }
  } else {
    console.warn(
      "GA4 Tracking skipped: Missing productId in cartItem",
      cartItem,
    );
  }

  localStorage.setItem("cartItems", JSON.stringify(getState().cart.cart));
  toast.success("Added to cart 🛒");
};

export const removeFromCartAsync =
  (productId, colorId, size) => (dispatch, getState) => {
    // Grab item details before removing so we can send them to GA4
    const item = getState().cart.cart.find(
      (i) =>
        i?.productId === productId &&
        i?.color?._id === colorId &&
        i?.size === size,
    );

    dispatch(removeFromCart({ productId, colorId, size }));

    if (item && productId) {
      try {
        trackRemoveFromCart(
          {
            id: productId,
            name: item?.name || item?.title || "Unknown Product",
            price: Number(item?.price) || 0,
            currency: item?.currency || "USD",
          },
          item?.quantity || 1,
        );
      } catch (error) {
        console.error("GA4 trackRemoveFromCart failed:", error);
      }
    }

    localStorage.setItem("cartItems", JSON.stringify(getState().cart.cart));
    toast.success("Removed from cart");
  };

export const updateQuantityAsync =
  (productId, colorId, size, quantity) => (dispatch, getState) => {
    dispatch(updateQuantity({ productId, colorId, size, quantity }));
    localStorage.setItem("cartItems", JSON.stringify(getState().cart.cart));
  };

export const clearCartAsync = () => (dispatch, getState) => {
  dispatch(clearCart());
  localStorage.removeItem("cartItems");
  toast.success("Cart cleared");
};
